import React, { useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { FaGithub, FaFacebook, FaEthereum } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { useAuth } from 'contexts/AuthContext';
import { API } from 'utilities/api';
import { connectMetaMask } from 'utilities/wallet';
import { T } from './authStyles';

/**
 * Social + wallet sign-in buttons.
 *
 * - OAuth providers full-page-navigate to `/auth/<provider>` on the
 *   backend; the callback hits OAuthCallback.jsx which handles routing.
 * - MetaMask runs in-page: connects, signs, calls verifyWallet for a
 *   JWT, then defers to `onSuccess` to do the actual redirect.
 *
 * Props:
 *   onSuccess(userPayload?)  — fired after a successful in-page auth
 *                              (wallet only). userPayload mirrors the
 *                              shape `verifyEmail` / `verifyWallet`
 *                              return so the parent can route on
 *                              `is_platform_admin`.
 */
export default function OAuthButtons({ onSuccess }) {
  const [walletLoading, setWalletLoading] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  const { login } = useAuth();

  const handleOAuth = (provider) => {
    setOauthError(null);
    window.location.href = `/auth/${provider}`;
  };

  const handleWallet = async () => {
    setOauthError(null);
    setWalletLoading(true);
    try {
      const { address, signature } = await connectMetaMask();
      const result = await API.verifyWallet({ signature, address });
      const token = result?.Token || result?.token;
      if (token) {
        login(token, result?.user || { address });
        onSuccess?.(result?.user || null);
      } else {
        setOauthError('Wallet verification failed. Please try again.');
      }
    } catch (err) {
      setOauthError(err?.message || 'Wallet connection failed');
    } finally {
      setWalletLoading(false);
    }
  };

  const hover = (e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.13)'; };
  const unhover = (e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {oauthError && (
        <div style={{
          background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, padding: '10px 14px', color: '#FCA5A5', fontSize: 13,
        }}>
          {oauthError}
        </div>
      )}
      <button style={T.oauthBtn} onClick={() => handleOAuth('google')}   onMouseEnter={hover} onMouseLeave={unhover}>
        <FcGoogle size={20} /> Continue with Google
      </button>
      <button style={T.oauthBtn} onClick={() => handleOAuth('facebook')} onMouseEnter={hover} onMouseLeave={unhover}>
        <FaFacebook size={18} color="#1877F2" /> Continue with Facebook
      </button>
      <button style={T.oauthBtn} onClick={() => handleOAuth('github')}   onMouseEnter={hover} onMouseLeave={unhover}>
        <FaGithub size={18} color="#fff" /> Continue with GitHub
      </button>
      <button style={T.oauthBtn} onClick={() => handleOAuth('twitter')}  onMouseEnter={hover} onMouseLeave={unhover}>
        <FaXTwitter size={18} color="#fff" /> Continue with X
      </button>
      <button style={{ ...T.oauthBtn, opacity: walletLoading ? 0.6 : 1 }} onClick={handleWallet} disabled={walletLoading}
        onMouseEnter={hover} onMouseLeave={unhover}>
        <FaEthereum size={18} color="#8B5CF6" /> {walletLoading ? 'Connecting…' : 'Connect Wallet'}
      </button>
    </div>
  );
}
